import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Search, ShieldCheck, Zap, TrendingUp, RefreshCw, Volume2, VolumeX, Activity, Waves } from 'lucide-react';

function App() {
  const [address, setAddress] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [alphaPicks, setAlphaPicks] = useState([]);
  const [isMuted, setIsMuted] = useState(false);
  const prevRadarRef = useRef([]);

  const API_BASE = "https://scannospro-api.kenbedagas.workers.dev";

  const playAlert = (isOrganic) => {
    if (isMuted) return;
    try {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      oscillator.type = isOrganic ? 'sawtooth' : 'square';
      oscillator.frequency.setValueAtTime(isOrganic ? 1000 : 660, audioCtx.currentTime);
      gainNode.gain.setValueAtTime(0.05, audioCtx.currentTime);
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      oscillator.start();
      oscillator.stop(audioCtx.currentTime + 0.3);
    } catch (e) { console.error("Audio error"); }
  };

  const fetchAlpha = async () => {
    try {
      const res = await axios.get(`${API_BASE}/`);
      if (res.data && res.data.alphaPicks) {
        const newPicks = res.data.alphaPicks;
        if (prevRadarRef.current.length > 0 && newPicks[0]?.address !== prevRadarRef.current[0]?.address) {
          playAlert(newPicks[0].organicStatus?.includes('ORGANIC'));
        }
        setAlphaPicks(newPicks);
        prevRadarRef.current = newPicks;
      }
    } catch (e) { console.error("Radar Offline"); }
  };

  useEffect(() => {
    fetchAlpha();
    const interval = setInterval(fetchAlpha, 20000);
    return () => clearInterval(interval);
  }, []);

  const analyzeToken = async (ca) => {
    const targetCA = ca || address;
    if (!targetCA || targetCA.length < 32) {
      alert("⚠️ Masukkan Contract Address Solana yang valid!");
      return;
    }
    setLoading(true);
    if (ca) setAddress(ca);
    
    try {
      const response = await axios.get(`${API_BASE}/?address=${targetCA}`);
      if (response.data && response.data.tokenInfo) {
        setData(response.data);
      }
    } catch (error) {
      alert("⚠️ Gagal terhubung ke AI ScannosPro.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#020617] text-slate-100 font-sans p-4 md:p-10 selection:bg-cyan-500/30">
      
      {/* Floating Audio Control */}
      <button 
        onClick={() => setIsMuted(!isMuted)}
        className="fixed bottom-8 right-8 z-50 p-4 rounded-full bg-slate-800 border border-white/10 shadow-2xl hover:scale-110 transition-all active:scale-95"
      >
        {isMuted ? <VolumeX className="text-red-400" /> : <Volume2 className="text-cyan-400" />}
      </button>

      {/* Header Section */}
      <div className="max-w-5xl mx-auto text-center mb-12">
        <div className="inline-flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/20 px-4 py-1 rounded-full mb-4">
          <RefreshCw size={14} className={`text-cyan-400 ${loading ? 'animate-spin' : 'animate-pulse'}`} />
          <span className="text-xs font-bold text-cyan-400 tracking-widest uppercase text-[10px]">Organic Sentry v3.0 Active</span>
        </div>
        <h1 className="text-6xl font-black tracking-tighter mb-2 italic">
          SCANNOS<span className="text-cyan-400">PRO</span>
        </h1>
        <p className="text-slate-500 font-medium tracking-widest uppercase text-[10px]">Detektor Taburan Pasir & Anti-Cluster</p>
      </div>

      {/* Search Bar */}
      <div className="max-w-2xl mx-auto flex gap-3 mb-16 p-2 bg-white/5 border border-white/10 rounded-2xl backdrop-blur-md shadow-2xl">
        <input 
          className="flex-1 bg-transparent border-none outline-none px-4 py-2 text-lg font-bold placeholder:text-slate-600 text-cyan-400"
          placeholder="PASTE CA UNTUK SCAN ORGANIK..."
          value={address}
          onChange={(e) => setAddress(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && analyzeToken()}
        />
        <button 
          onClick={() => analyzeToken()}
          disabled={loading}
          className="bg-cyan-500 hover:bg-cyan-400 disabled:bg-slate-800 text-slate-900 px-8 py-3 rounded-xl font-black flex items-center gap-2 transition-all shadow-lg shadow-cyan-500/20"
        >
          {loading ? "SCANNING..." : <><Search size={18}/> EXECUTE</>}
        </button>
      </div>

      {/* Main Analysis Display */}
      {data && (
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 mb-20">
          
          <div className="lg:col-span-7 space-y-6">
            <div className="bg-slate-900 border border-white/10 p-2 rounded-3xl overflow-hidden shadow-2xl">
              <iframe 
                key={address}
                src={`https://dexscreener.com/solana/${address}?embed=1&theme=dark&trades=0&info=0`} 
                className="w-full h-[550px] rounded-2xl"
                title="DexScreener Chart"
              ></iframe>
            </div>

            {/* BUBBLEMAPS INTEGRATION (FIXED) */}
            <div className="bg-slate-900 border border-purple-500/30 p-2 rounded-3xl overflow-hidden shadow-2xl">
               <div className="flex justify-between items-center p-3">
                  <div className="flex items-center gap-2 text-purple-400 font-bold text-xs uppercase tracking-widest">
                    <Waves size={16} className="animate-pulse" /> BubbleMaps Organic Sensor
                  </div>
                  <button 
                    onClick={() => window.open(`https://v2.bubblemaps.io/map?address=${address}&chain=solana`, '_blank')}
                    className="text-[10px] bg-purple-500/20 hover:bg-purple-500/40 text-purple-300 px-2 py-1 rounded border border-purple-500/30 transition-all"
                  >
                    OPEN FULL MAP ↗
                  </button>
               </div>
               <div className="relative w-full aspect-video rounded-2xl overflow-hidden bg-black">
                  <img 
                    key={address}
                    src={`https://s0.wp.com/mshots/v1/https://v2.bubblemaps.io/map?address=${address}&chain=solana?width=1024&height=768`} 
                    className="w-full h-full object-cover"
                    alt="BubbleMaps Preview"
                    onError={(e) => { e.target.src='https://via.placeholder.com/800x600?text=BubbleMaps+Loading...'; }}
                  />
               </div>
            </div>
          </div>

          <div className="lg:col-span-5 space-y-6">
            <div className="bg-slate-900/50 border border-cyan-500/30 p-8 rounded-3xl backdrop-blur-xl h-full flex flex-col">
              <div className="text-center mb-6">
                <p className="text-slate-500 text-xs font-black tracking-widest mb-2 uppercase text-[10px]">Scannos Organic Score</p>
                <h2 className={`text-[120px] leading-none font-black ${data.scannosScore > 70 ? 'text-green-400' : 'text-red-400'}`}>
                  {data.scannosScore}<span className="text-3xl">%</span>
                </h2>
                <div className={`mt-2 inline-block px-6 py-2 rounded-lg font-black text-xl uppercase ${data.scannosScore > 70 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                  {data.organicStatus}
                </div>
              </div>

              <div className="bg-slate-950/80 border border-white/5 p-6 rounded-2xl flex-1 overflow-y-auto max-h-[300px] mb-6">
                <p className="text-sm leading-relaxed text-slate-300 font-medium">{data.aiInsight}</p>
              </div>

              <button 
                onClick={() => window.open(`https://jup.ag/swap/SOL-${address}`, '_blank')}
                className="w-full py-6 bg-[#32E0C4] hover:scale-[1.02] text-slate-950 rounded-2xl font-black text-2xl shadow-2xl transition-all"
              >
                SNIPE ON JUPITER
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto space-y-20">
        <section>
          <h3 className="flex items-center gap-3 text-2xl font-black italic mb-8 border-l-4 border-cyan-500 pl-4 uppercase tracking-tighter">
            <Zap className="text-cyan-400 fill-cyan-400" /> Radar 1: New Organic Potential
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {alphaPicks.slice(0, 4).map((pick, i) => (
              <RadarCard key={i} pick={pick} onAnalyze={analyzeToken} isRadar1={true} />
            ))}
          </div>
        </section>

        <section>
          <h3 className="flex items-center gap-3 text-2xl font-black italic mb-8 border-l-4 border-orange-500 pl-4 uppercase tracking-tighter">
            <TrendingUp className="text-orange-500" /> Radar 2: Early Rebound Radar
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {alphaPicks.slice(4, 8).map((pick, i) => (
              <RadarCard key={i} pick={pick} onAnalyze={analyzeToken} isRadar1={false} />
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

function RadarCard({ pick, onAnalyze, isRadar1 }) {
  return (
    <div className="bg-slate-900 border border-white/5 rounded-3xl overflow-hidden hover:border-cyan-500/50 transition-all group hover:-translate-y-2">
      <div className="h-40 bg-slate-950 relative overflow-hidden">
        {pick.img ? (
            <img src={pick.img} className="w-full h-full object-cover opacity-60 group-hover:opacity-100" alt="token" onError={(e) => e.target.src='https://via.placeholder.com/150?text=NO+IMAGE'} />
        ) : (
            <Zap className="absolute inset-0 m-auto opacity-10" size={60} />
        )}
        <div className="absolute top-4 right-4 flex gap-2">
            <span className={`text-slate-950 text-[10px] font-black px-2 py-1 rounded uppercase ${isRadar1 ? 'bg-green-500' : 'bg-orange-500'}`}>
                {isRadar1 ? 'GEM' : 'REBOUND'}
            </span>
            <span className="bg-slate-800 text-cyan-400 text-[10px] font-black px-2 py-1 rounded border border-cyan-500/30">
                {pick.age}
            </span>
        </div>
      </div>
      <div className="p-6">
        <h4 className="text-lg font-black mb-1 truncate text-cyan-400">{pick.name}</h4>
        <p className={`text-[10px] font-bold mb-4 ${pick.organicStatus?.includes('ORGANIC') ? 'text-green-400' : 'text-yellow-400'}`}>
            {pick.organicStatus} // {pick.reboundStatus}
        </p>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => onAnalyze(pick.address)} className="py-2 bg-slate-800 rounded-lg text-xs font-bold hover:bg-slate-700">ANALYZE</button>
          <button onClick={() => window.open(`https://jup.ag/swap/SOL-${pick.address}`, '_blank')} className="py-2 bg-cyan-500 text-slate-950 rounded-lg text-xs font-black">JUPITER</button>
        </div>
      </div>
    </div>
  );
}

export default App;